
import {
  ArrowUpRight,
  BarChart3,
  Database,
  History,
  ShieldCheck,
  UserMinus,
  UserPlus,
  MonitorCheck,
} from 'lucide-react';

import { useNavigate } from 'react-router-dom';

import type { PublicReportItem } from '../types/publicReport.types';

interface ReportCardProps {
  report: PublicReportItem;
  index: number;
}

const ALLOWED_REPORT_IDS = [
  'activity-log',
  'summary-report',
  'access-creation',
  'access-revocation',
  'access-modification',
  'tracking-report',
] as const;

const REPORT_ROUTES: Record<
  (typeof ALLOWED_REPORT_IDS)[number],
  string
> = {
  'activity-log': '/public-report/activity-log',
  'summary-report': '/public-report/summary-report',
  'access-creation': '/public-report/access-creation',
  'access-revocation': '/public-report/access-revocation',
  'access-modification': '/public-report/access-modification',
  'tracking-report': '/public-report/tracking-report',
};

const isAllowedReport = (
  reportId: string,
): reportId is (typeof ALLOWED_REPORT_IDS)[number] =>
  ALLOWED_REPORT_IDS.some((allowedId) => allowedId === reportId);

const getReportIcon = (reportId: string) => {
  switch (reportId) {
    case 'activity-log':
      return History;
    case 'summary-report':
      return BarChart3;
    case 'access-creation':
      return UserPlus;
    case 'access-revocation':
      return UserMinus;
    case 'access-modification':
      return ShieldCheck;
    case 'tracking-report':
      return MonitorCheck;
    default:
      return Database;
  }
};

const getIconStyle = (reportId: string): string => {
  switch (reportId) {
    case 'activity-log':
      return 'bg-blue-50 text-blue-600 group-hover:bg-blue-100';
    case 'summary-report':
      return 'bg-violet-50 text-violet-600 group-hover:bg-violet-100';
    case 'access-creation':
      return 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100';
    case 'access-revocation':
      return 'bg-rose-50 text-rose-600 group-hover:bg-rose-100';
    case 'access-modification':
      return 'bg-teal-50 text-teal-600 group-hover:bg-teal-100';
    case 'tracking-report':
      return 'bg-cyan-50 text-cyan-700 group-hover:bg-cyan-100';
    default:
      return 'bg-slate-100 text-slate-600 group-hover:bg-slate-200';
  }
};

const getAccentStyle = (reportId: string): string => {
  switch (reportId) {
    case 'activity-log':
      return 'group-hover:bg-blue-500';
    case 'summary-report':
      return 'group-hover:bg-violet-500';
    case 'access-creation':
      return 'group-hover:bg-emerald-500';
    case 'access-revocation':
      return 'group-hover:bg-rose-500';
    case 'access-modification':
      return 'group-hover:bg-teal-500';
    case 'tracking-report':
      return 'group-hover:bg-cyan-500';
    default:
      return 'group-hover:bg-teal-500';
  }
};

const getArrowStyle = (reportId: string): string => {
  switch (reportId) {
    case 'activity-log':
      return 'group-hover:border-blue-200 group-hover:bg-blue-50 group-hover:text-blue-600';
    case 'summary-report':
      return 'group-hover:border-violet-200 group-hover:bg-violet-50 group-hover:text-violet-600';
    case 'access-creation':
      return 'group-hover:border-emerald-200 group-hover:bg-emerald-50 group-hover:text-emerald-600';
    case 'access-revocation':
      return 'group-hover:border-rose-200 group-hover:bg-rose-50 group-hover:text-rose-600';
    case 'access-modification':
      return 'group-hover:border-teal-200 group-hover:bg-teal-50 group-hover:text-teal-600';
    case 'tracking-report':
      return 'group-hover:border-cyan-200 group-hover:bg-cyan-50 group-hover:text-cyan-700';
    default:
      return 'group-hover:border-teal-200 group-hover:bg-teal-50 group-hover:text-teal-600';
  }
};

const ReportCard = ({ report, index }: ReportCardProps) => {
  const navigate = useNavigate();

  if (!isAllowedReport(report.id)) {
    return null;
  }

  const Icon = getReportIcon(report.id);
  const iconStyle = getIconStyle(report.id);
  const accentStyle = getAccentStyle(report.id);
  const arrowStyle = getArrowStyle(report.id);

  const handleOpenReport = () => {
    if (!isAllowedReport(report.id)) {
      return;
    }

    navigate(REPORT_ROUTES[report.id]);
  };

  return (
    <button
      type="button"
      onClick={handleOpenReport}
      aria-label={`Open ${report.title}`}
      className="
        group relative flex min-h-[138px] w-full items-center gap-4
        overflow-hidden rounded-xl border border-slate-200 bg-white
        px-4 py-4 text-left shadow-[0_1px_3px_rgba(15,23,42,0.04)]
        transition-all duration-200 hover:-translate-y-0.5
        hover:border-slate-300 hover:shadow-[0_8px_24px_rgba(15,23,42,0.08)]
        focus:outline-none focus:ring-2 focus:ring-teal-500
        focus:ring-offset-2 sm:px-5
      "
    >
      {/* LEFT ACCENT */}
      <span
        className={`
          absolute bottom-0 left-0 top-0 w-[3px]
          bg-slate-200 transition-colors duration-200 ${accentStyle}
        `}
      />

      {/* NUMBER */}
      <span
        className="absolute right-3 top-3 text-[10px] font-semibold tracking-wider text-slate-300"
        aria-hidden="true"
      >
        {String(index + 1).padStart(2, '0')}
      </span>

      {/* ICON */}
      <div
        className={`
          flex h-11 w-11 shrink-0 items-center justify-center
          rounded-lg transition-colors duration-200 ${iconStyle}
        `}
        aria-hidden="true"
      >
        <Icon size={20} strokeWidth={1.9} />
      </div>

      {/* CONTENT */}
      <div className="min-w-0 flex-1 pr-3">
        <h2
          className="
            truncate text-[15px] font-semibold leading-5 text-slate-800
            transition-colors group-hover:text-slate-950 sm:text-base
          "
        >
          {report.title}
        </h2>

        <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500 sm:text-[13px]">
          {report.description}
        </p>

        {report.category && (
          <div className="mt-2.5 flex items-center gap-1.5">
            <span
              className="h-1.5 w-1.5 rounded-full bg-teal-500"
              aria-hidden="true"
            />

            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
              {report.category}
            </span>
          </div>
        )}
      </div>

      {/* ARROW */}
      <div
        className={`
          flex h-7 w-7 shrink-0 items-center justify-center rounded-full
          border border-slate-200 bg-white text-slate-400
          transition-all duration-200 ${arrowStyle}
        `}
        aria-hidden="true"
      >
        <ArrowUpRight size={15} strokeWidth={2} />
      </div>
    </button>
  );
};

export default ReportCard;
